/**
 * Tour de bienvenida a la aplicación
 * Este tour guía a los usuarios mostrándoles las funcionalidades básicas
 * y cómo acceder a los tutoriales de cada proceso.
 */

import { Tour, TourStep } from '../models/interfaces'
import { saveTourState } from '../utils/helpers'
import router from '@/router'

/**
 * Tour de bienvenida para usuarios nuevos
 * No depende del rol de usuario - se muestra a todos
 */
export const WelcomeTour: Tour = {
  id: 'welcome-tour',
  name: 'Tour de Bienvenida a GENIO',
  steps: [
    // Paso 1: Bienvenida general (Home.vue)
    {
      target: 'body', // Usar body como fallback seguro
      title: '¡Bienvenido a GENIO!',
      content: 'Te guiaremos a través de las principales funcionalidades de la plataforma.',
      placement: 'center',
      beforeStep: async () => {
        // Asegurarse de que estamos en Home
        if (router.currentRoute.name !== 'home') {
          await router.push({ name: 'home' })
          // Esperar a que se cargue la página
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      },
    },

    // Paso 2: Explicación del menú circular
    {
      target: '.circular-image-container',
      title: 'Menú Principal',
      content: 'Este es nuestro menú principal donde puedes acceder a todos los tutoriales de los procesos de GENIO.',
      placement: 'bottom',
      highlightPadding: 10,
    },

    // Paso 3: Explicación de los iconos de proceso
    {
      target: '.icon-circle',
      title: 'Procesos',
      content: 'Cada ícono representa un proceso diferente en el sistema. Puedes hacer clic en cualquiera para acceder a sus tutoriales',
      placement: 'bottom',
      highlightPadding: 5,
      afterStep: async () => {
        const event = new CustomEvent('is-mini-drawer', { bubbles: true })
        document.dispatchEvent(event)
      },
    },

    // Paso 4: Guiar al usuario hacia los tutoriales
    {
      target: '.button-help',
      title: 'Acceso a Tutoriales',
      content: 'Haz clic en cualquier ícono para acceder a los tutoriales específicos de ese proceso o en el botón de ayuda para acceder a los tutoriales.',
      placement: 'top',
      highlightPadding: 5,
      afterStep: async () => {
        // Simular navegación a la página de ayuda
        await router.push({ name: 'help' })
        // Esperar a que se cargue la página
        await new Promise(resolve => setTimeout(resolve, 1000))
      },
    },

    // Paso 5: Explicación de la página de ayuda (HelpPage.vue)
    {
      target: '.help-container',
      title: 'Centro de Ayuda',
      content: 'Aquí encontrarás todos los recursos educativos organizados por proceso.',
      placement: 'top',
      highlightPadding: 10,
      scrollIntoView: false,
      beforeStep: async () => {
        // Asegurarse de que estamos en la página de ayuda
        if (!router.currentRoute.path.includes('help')) {
          await router.push({ name: 'help' })
          // Esperar a que se cargue la página y sus recursos (aumentado a 3000ms)
          await new Promise(resolve => setTimeout(resolve, 3000))
        }

        // Primero hacer scroll al inicio de la página
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Esperar un poco más y luego hacer scroll al elemento específico
        setTimeout(() => {
          const element = document.querySelector('.help-container')
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          } else {
            console.error('Elemento .help-container no encontrado')
          }
        }, 3000) // Aumentado a 3000ms para dar más tiempo
      },
    },

    // Paso 6: Explicación del buscador
    {
      target: '.header', // Seleccionar un contenedor que incluya tanto la barra como las pestañas
      title: 'Buscador y Categorías',
      content: 'Usa el buscador para encontrar tutoriales específicos o navega por las pestañas de las diferentes categorías de procesos.',
      placement: 'bottom',
      highlightPadding: 20, // Aumentamos el padding para cubrir más área
      beforeStep: async () => {
        // Asegurar que la vista está posicionada correctamente
        window.scrollTo({ top: 100, behavior: 'smooth' })

        // Esperar para que el scroll se complete
        await new Promise(resolve => setTimeout(resolve, 500))

        // Intentar encontrar el elemento y hacer scroll si es necesario
        const searchBar = document.querySelector('.search-bar')
        if (searchBar) {
          searchBar.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      },
    },

    // Paso 7: Explicación de los videos
    {
      target: '.videos-container .title, .videos-container h2, .videos-container > *:first-child',
      title: 'Tutoriales en Video',
      content: 'Aquí encontrarás videos explicativos sobre cada funcionalidad del sistema.',
      placement: 'top',
      highlightPadding: 15,
      scrollIntoView: true,
      // Asegurar que el contenedor de videos esté visible
      beforeStep: async () => {
        // Método agresivo para forzar scroll al inicio
        const forceScrollToTop = () => {
          // Cancelar cualquier scroll activo
          try {
            window.stop()
          } catch (e) {
            console.error('No se pudo cancelar scrolls activos')
          }

          // Forzar posición 0 usando múltiples métodos
          window.scrollTo(0, 0)
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0

          // También intentar con scroll nativo
          window.scroll(0, 0)

          // Como último recurso, intentar con scrollIntoView
          document.body.scrollIntoView({ block: 'start', inline: 'start' })

          return window.scrollY
        }

        // Hacer varios intentos para asegurar que el scroll es 0
        let currentPos = forceScrollToTop()

        // Esperar un poco y verificar de nuevo
        await new Promise(resolve => setTimeout(resolve, 500))
        currentPos = forceScrollToTop()

        // Esperar más tiempo e intentar de nuevo
        await new Promise(resolve => setTimeout(resolve, 1000))
        currentPos = forceScrollToTop()

        // Buscar elementos dentro del contenedor para apuntar más específicamente
        const targetSelectors = ['.videos-container .title', '.videos-container h2', '.videos-container > *:first-child']
        let foundTarget = null

        for (const selector of targetSelectors) {
          const element = document.querySelector(selector)
          if (element) {
            foundTarget = element
            break
          }
        }

        if (foundTarget) {
          // No hacemos nada más, dejamos que el tour maneje el scroll hacia el elemento específico
        } else {
          console.error('No se encontró elemento específico, usando contenedor general')
          // Si no se encuentra un elemento específico, se usará el selector general
        }

        // Esperar a que se complete la operación
        await new Promise(resolve => setTimeout(resolve, 1000))
      },
    },

    // Paso 8: Reproducción de un video específico
    {
      target: '.youtube-player',
      title: 'Reproducción de Videos',
      content: 'Haz clic en cualquier video para verlo. Puedes pausarlo y reanudarlo en cualquier momento.',
      placement: 'bottom',
      highlightPadding: 10,
      beforeStep: async () => {
        // Buscar el primer componente de YouTube player
        const youtubePlayer = document.querySelector('youtube-player')

        if (youtubePlayer) {
          youtubePlayer.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else {
          console.error('No se encontró ningún player de YouTube')
        }

        // Esperar para completar la acción
        await new Promise(resolve => setTimeout(resolve, 1500))
      },
      afterStep: async () => {
        const event = new CustomEvent('is-mini-drawer', { bubbles: true })
        document.dispatchEvent(event)
      },
    },

    // Paso 9: Explicación de tickets y requerimientos
    {
      target: '.requirement-tickets',
      title: 'Reportar Requerimientos',
      content: 'Puedes reportar tickets haciendo clic en el logo de GENIO. Esto te permitirá solicitar mejoras o reportar fallos en la plataforma.',
      placement: 'bottom',
      highlightPadding: 10,
      afterStep: async () => {
        await new Promise(resolve => setTimeout(resolve, 1000))
        const event = new CustomEvent('open-ticket-dialog', { bubbles: true })
        document.dispatchEvent(event)
        await new Promise(resolve => setTimeout(resolve, 1000))
      },
    },

    // Paso 10: Explicación del formulario de tickets
    {
      target: '.ticket-dialog',
      title: 'Formulario de Requerimientos',
      content: 'En este formulario puedes seleccionar el tipo de reporte (Fallo o Mejora), escribir una descripción detallada y adjuntar imágenes o videos que ayuden a entender mejor tu solicitud.',
      placement: 'bottom',
      highlightPadding: 30,
      afterStep: async () => {
        localStorage.setItem('tour-just-finished', 'true')
        const event = new CustomEvent('close-ticket-dialog', { bubbles: true })
        await router.push({ name: 'home' })

        document.dispatchEvent(event)
      },
    },
  ],

  // Función que se ejecuta al completar el tour
  onComplete: () => {
    saveTourState('welcome-tour', true)

    // Marcar que acabamos de terminar el tour para evitar reinicio automático
    localStorage.setItem('tour-just-finished', 'true')

    // Emitir evento de finalización en lugar de mostrar un alert
    const finishEvent = new CustomEvent('finish-welcome-tour', {
      bubbles: true,
      detail: { skipped: false },
    })
    document.dispatchEvent(finishEvent)
  },

  // Función que se ejecuta si el usuario omite el tour
  onSkip: () => {
    saveTourState('welcome-tour', false)

    // Guardar en localStorage que el usuario omitió el tour
    localStorage.setItem('welcome-tour-skipped', 'true')

    // Marcar que acabamos de terminar el tour para evitar reinicio automático
    localStorage.setItem('tour-just-finished', 'true')

    // Emitir evento de finalización con indicador de omitido
    const skipEvent = new CustomEvent('finish-welcome-tour', {
      bubbles: true,
      detail: { skipped: true },
    })
    document.dispatchEvent(skipEvent)
  },
}
